import { useEffect, useMemo, useRef, useState } from 'react'
import { useAppStore } from '@/store/useAppStore'
import { loadQueue, pendingWriteCount, clearAllPendingWrites } from '@/lib/writeQueue'
import {
  X, RefreshCw, AlertCircle, WifiOff, CheckCircle2, Clock, Radio, Database, Trash2, ChevronDown,
  Microscope, Copy,
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useToast } from '@/components/ui/Toast'

const HEALTHY_LATENCY_MS = 500
const SLOW_LATENCY_MS = 1500

function formatAge(ms) {
  const s = Math.max(0, Math.round(ms / 1000))
  if (s < 60) return `${s}s ago`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  return `${h}h ago`
}

function StatusDot({ tone }) {
  const bg = tone === 'good' ? 'bg-green-500' : tone === 'warn' ? 'bg-yellow-400' : tone === 'bad' ? 'bg-red-500' : 'bg-text-faint'
  return <span className={`w-1.5 h-1.5 rounded-full ${bg} flex-shrink-0`} />
}

function ForensicRow({ label, value, tone = 'idle' }) {
  return (
    <div className="flex items-center justify-between gap-2 text-[10.5px]">
      <span className="text-text-muted flex-shrink-0">{label}</span>
      <div className="flex items-center gap-1.5 min-w-0">
        <StatusDot tone={tone} />
        <span className="text-text truncate font-mono text-[10px]">{value}</span>
      </div>
    </div>
  )
}

function opLabel(entry) {
  const verbs = { insert: 'create', update: 'edit', delete: 'delete', upsert: 'save' }
  const nouns = { tasks: 'task', projects: 'project', sections: 'section', project_members: 'member', project_invitations: 'invite', goals: 'goal' }
  const verb = verbs[entry.op] || entry.op
  const noun = nouns[entry.table] || entry.table
  return `${verb} ${noun}`
}

function entityLabelFor(entry) {
  // Best-effort: pull a human name out of the descriptor. INSERT carries full row;
  // UPDATE carries a partial — fall back to id if nothing useful is there.
  const d = entry.data || {}
  return d.title || d.name || null
}

export function SyncDiagnosticPanel({ errors, onDismissError, onClearErrors, onClose }) {
  const realtimeStatus = useAppStore((s) => s._realtimeStatus)
  const realtimeStatusAt = useAppStore((s) => s._realtimeStatusAt)
  const lastPing = useAppStore((s) => s._lastPing)
  const pingHealth = useAppStore((s) => s.pingHealth)
  const retryPendingWrites = useAppStore((s) => s.retryPendingWrites)
  const retryPendingWritesRaw = useAppStore((s) => s.retryPendingWritesRaw)
  const syncState = useAppStore((s) => s._syncState)
  const inFlight = useAppStore((s) => s._inFlight)
  const writeFailures = useAppStore((s) => s._writeFailures)
  const toast = useToast()

  const [queue, setQueue] = useState(() => loadQueue())
  const [now, setNow] = useState(() => Date.now())
  const [retrying, setRetrying] = useState(false)
  const [retryStatus, setRetryStatus] = useState(null) // { tone, msg }
  const [pinging, setPinging] = useState(false)
  const [expandedOps, setExpandedOps] = useState(false)
  const [expandedFailure, setExpandedFailure] = useState(false)

  const mountedRef = useRef(true)
  useEffect(() => () => { mountedRef.current = false }, [])

  // Live timers — refresh queue + clock every second while open
  useEffect(() => {
    const t = setInterval(() => {
      setQueue(loadQueue())
      setNow(Date.now())
    }, 1000)
    return () => clearInterval(t)
  }, [])

  // Ping on mount + every 30s while open
  useEffect(() => {
    let cancelled = false
    async function doPing() {
      if (cancelled) return
      setPinging(true)
      await pingHealth()
      if (!cancelled) setPinging(false)
    }
    doPing()
    const t = setInterval(doPing, 30000)
    return () => { cancelled = true; clearInterval(t) }
  }, [pingHealth])

  async function handleManualPing() {
    setPinging(true)
    await pingHealth()
    setPinging(false)
  }

  async function handleRetry() {
    setRetrying(true)
    setRetryStatus({ tone: 'info', msg: 'Retrying…' })
    try {
      // During a confirmed auth-lock wedge the client is hung — retry via the lock-free path,
      // which bypasses the wedged supabase client entirely.
      const flushed = syncState === 'stuck-reconnecting'
        ? await retryPendingWritesRaw()
        : await retryPendingWrites()
      const remaining = pendingWriteCount()
      setQueue(loadQueue())
      if (remaining === 0) {
        setRetryStatus({ tone: 'good', msg: flushed ? 'All changes synced.' : 'Already in sync — nothing pending.' })
        toast({ message: flushed ? 'Changes synced' : 'Nothing to sync — all clear' })
      } else if (flushed) {
        setRetryStatus({ tone: 'warn', msg: `Synced some changes. ${remaining} still pending.` })
      } else {
        setRetryStatus({ tone: 'bad', msg: "Couldn't reach the server. Changes are saved locally." })
      }
    } catch (e) {
      setRetryStatus({ tone: 'bad', msg: e?.message || 'Retry failed.' })
    } finally {
      if (mountedRef.current) setRetrying(false)
    }
  }

  async function handleCopyForensics() {
    try {
      const payload = JSON.stringify({
        capturedAt: new Date().toISOString(),
        realtimeStatus,
        realtimeStatusAt: realtimeStatusAt ? new Date(realtimeStatusAt).toISOString() : null,
        lastPing,
        inFlight,
        pendingQueue: queue.map((e) => ({ table: e.table, op: e.op, attempts: e.attempts, ageMs: now - (e.ts || now) })),
        writeFailures,
      }, null, 2)
      await navigator.clipboard.writeText(payload)
      toast({ message: 'Forensics copied to clipboard' })
    } catch {
      toast({ message: 'Copy failed — open devtools and inspect window.__prjctSyncForensics' })
    }
  }

  function handleClearQueue() {
    const confirmed = window.confirm('Discard all pending writes? Local changes that have not synced will be lost.')
    if (!confirmed) return
    const n = clearAllPendingWrites()
    setQueue(loadQueue())
    setRetryStatus({ tone: 'warn', msg: n ? `Discarded ${n} pending write${n !== 1 ? 's' : ''}.` : 'Queue already empty.' })
    toast({ message: n ? `Discarded ${n} pending write${n !== 1 ? 's' : ''}` : 'Queue already empty' })
    window.dispatchEvent(new CustomEvent('prjct-write-synced'))
  }

  // "Stalled" — the panel was lying when realtime said 'connected' but writes were
  // pending forever. Forensics proved that pings can succeed at <2s while writes still
  // time out at 20s — so ping latency is NOT a reliable signal for whether writes are
  // landing. The honest signal is the queue itself: any entry older than 15s means a
  // write has missed at least one full retry budget and we're stuck regardless of what
  // the WS or ping report.
  const hasStaleQueueEntry = queue.some((e) => now - (e.ts || now) > 15000)
  const isStalled = realtimeStatus === 'connected' && hasStaleQueueEntry

  const rtTone = isStalled ? 'warn'
    : realtimeStatus === 'connected' ? 'good'
    : realtimeStatus === 'connecting' ? 'warn'
    : realtimeStatus === 'degraded' ? 'bad'
    : realtimeStatus === 'offline' ? 'bad'
    : 'idle'

  const rtLabel = isStalled ? 'Stalled'
    : ({
      connected: 'Connected',
      connecting: 'Connecting…',
      degraded: 'Degraded',
      offline: 'Offline',
      idle: 'Idle',
    }[realtimeStatus] || realtimeStatus)

  const dbTone = !lastPing ? 'idle'
    : !lastPing.ok ? 'bad'
    : lastPing.latencyMs <= HEALTHY_LATENCY_MS ? 'good'
    : lastPing.latencyMs <= SLOW_LATENCY_MS ? 'warn'
    : 'bad'

  const dbLabel = !lastPing ? 'Checking…'
    : !lastPing.ok ? (lastPing.error || 'Unreachable')
    : `${lastPing.latencyMs}ms`

  // Group queue by op-noun for the summary line
  const opSummary = useMemo(() => {
    const counts = {}
    for (const e of queue) {
      const key = opLabel(e)
      counts[key] = (counts[key] || 0) + 1
    }
    return Object.entries(counts)
      .map(([k, n]) => `${n} ${k}${n !== 1 ? 's' : ''}`)
      .join(' · ')
  }, [queue])

  return (
    <div className="w-96 max-w-[calc(100vw-2rem)] bg-surface border border-border rounded-xl shadow-xl shadow-black/30 overflow-hidden">
      {/* ── Header */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-border">
        <div className="flex items-center gap-2">
          <AlertCircle size={13} className="text-accent flex-shrink-0" />
          <p className="text-xs font-semibold text-text">Sync diagnostics</p>
        </div>
        <button onClick={onClose} className="text-text-faint hover:text-text transition-colors" aria-label="Close">
          <X size={13} />
        </button>
      </div>

      {/* ── Connection section */}
      <div className="px-3 py-2.5 border-b border-border space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Radio size={12} className="text-text-faint flex-shrink-0" />
            <span className="text-[11px] text-text-muted">Realtime</span>
          </div>
          <div className="flex items-center gap-1.5 min-w-0">
            <StatusDot tone={rtTone} />
            <span className="text-[11px] text-text truncate">{rtLabel}</span>
            {realtimeStatusAt > 0 && (
              <span className="text-[10px] text-text-faint flex-shrink-0">{formatAge(now - realtimeStatusAt)}</span>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Database size={12} className="text-text-faint flex-shrink-0" />
            <span className="text-[11px] text-text-muted">Database</span>
          </div>
          <div className="flex items-center gap-1.5 min-w-0">
            <StatusDot tone={dbTone} />
            <span className="text-[11px] text-text truncate">{dbLabel}</span>
            <button
              onClick={handleManualPing}
              disabled={pinging}
              className="text-text-faint hover:text-text transition-colors disabled:opacity-40 flex-shrink-0"
              aria-label="Ping now"
              title="Ping now"
            >
              <RefreshCw size={10} className={pinging ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
        {inFlight > 0 && (
          <div className="flex items-center gap-2 pt-1">
            <Clock size={12} className="text-orange-400 flex-shrink-0 animate-pulse" />
            <span className="text-[11px] text-orange-400">{inFlight} write{inFlight !== 1 ? 's' : ''} in flight</span>
          </div>
        )}
      </div>

      {/* ── Pending writes */}
      <div className="px-3 py-2.5 border-b border-border">
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <div className="flex items-center gap-2">
            <Clock size={12} className="text-text-faint" />
            <span className="text-[11px] font-semibold text-text">Pending ({queue.length})</span>
          </div>
          {queue.length > 0 && (
            <button
              onClick={() => setExpandedOps((v) => !v)}
              className="text-[10px] text-text-faint hover:text-text transition-colors flex items-center gap-0.5"
            >
              {expandedOps ? 'Hide' : 'Show'} details
              <ChevronDown size={10} className={`transition-transform ${expandedOps ? 'rotate-180' : ''}`} />
            </button>
          )}
        </div>

        {queue.length === 0 ? (
          <div className="flex items-center gap-2">
            <CheckCircle2 size={12} className="text-green-500" />
            <span className="text-[11px] text-text-muted">No pending writes.</span>
          </div>
        ) : (
          <>
            <p className="text-[11px] text-text-muted leading-snug">{opSummary}</p>
            <AnimatePresence initial={false}>
              {expandedOps && (
                <motion.ul
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.18, ease: [0.23, 1, 0.32, 1] }}
                  className="overflow-hidden mt-2 max-h-40 overflow-y-auto divide-y divide-border/60 border-t border-border/60"
                >
                  {queue.map((entry) => {
                    const label = entityLabelFor(entry)
                    const age = now - (entry.ts || now)
                    const stale = age > 15000
                    return (
                      <li key={entry.id} className="py-1.5 flex items-start gap-2">
                        <StatusDot tone={stale ? 'warn' : 'idle'} />
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] text-text leading-snug truncate">
                            {opLabel(entry)}
                            {label && <span className="text-text-muted"> · {label}</span>}
                          </p>
                          <p className="text-[10px] text-text-faint">
                            {formatAge(age)}{entry.attempts ? ` · ${entry.attempts} attempt${entry.attempts !== 1 ? 's' : ''}` : ''}
                          </p>
                        </div>
                      </li>
                    )
                  })}
                </motion.ul>
              )}
            </AnimatePresence>
          </>
        )}
      </div>

      {/* ── Last failure forensics
          Captured at the moment a write timed out. Answers the only question that
          matters when "Connected" looks green but writes still hang: were reads
          working, was the WebSocket actually 'joined', and where did the 20s go? */}
      {writeFailures.length > 0 && (() => {
        const f = writeFailures[0]
        const pingTone = !f.ping ? 'idle' : f.ping.ok ? 'good' : 'bad'
        const rawProbeTone = !f.rawProbe ? 'idle' : f.rawProbe.ok ? 'good' : 'bad'
        const verdictTone = f.verdict === 'auth-lock-wedge' ? 'bad' : f.verdict === 'network-or-server' ? 'warn' : 'idle'
        const verdictLabel = f.verdict === 'auth-lock-wedge' ? 'Auth-lock wedge (client stuck, network OK)'
          : f.verdict === 'network-or-server' ? 'Network / server unreachable'
          : f.verdict || 'inconclusive'
        const channelsLabel = f.realtime.channels.length === 0
          ? 'no channels'
          : f.realtime.channels.map((c) => c.state || '?').join(' · ')
        const channelsTone = f.realtime.channels.every((c) => c.state === 'joined') ? 'good' : 'bad'
        // Older forensics entries (pre-severity) won't have the field — default to 'transient'
        // since that was the only path captured before this change.
        const severity = f.severity || 'transient'
        const isPermanent = severity === 'permanent'
        return (
          <div className="border-b border-border">
            <button
              onClick={() => setExpandedFailure((v) => !v)}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 hover:bg-surface-raised/40 transition-colors"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Microscope size={12} className={`${isPermanent ? 'text-red-400' : 'text-yellow-400'} flex-shrink-0`} />
                <span className="text-[11px] font-semibold text-text">Last failure forensics</span>
                <span
                  className={`text-[9.5px] font-semibold uppercase tracking-wide px-1.5 py-[1px] rounded ${
                    isPermanent
                      ? 'bg-red-500/15 text-red-400'
                      : 'bg-yellow-500/15 text-yellow-400'
                  }`}
                  title={isPermanent ? 'Data discarded, state restored — toast was shown' : 'Queued, retrying — no toast'}
                >
                  {severity}
                </span>
                <span className="text-[10px] text-text-faint truncate">{formatAge(now - f.at)}</span>
              </div>
              <ChevronDown size={11} className={`text-text-faint transition-transform ${expandedFailure ? 'rotate-180' : ''}`} />
            </button>
            <AnimatePresence initial={false}>
              {expandedFailure && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.18, ease: [0.23, 1, 0.32, 1] }}
                  className="overflow-hidden"
                >
                  <div className="px-3 pb-2.5 space-y-1.5">
                    <p className="text-[10.5px] text-text-muted leading-snug pb-1">{f.label}</p>

                    <ForensicRow
                      label="Ping at failure"
                      tone={pingTone}
                      value={f.ping?.ok ? `${f.ping.latencyMs}ms ✓ reads working` : `failed: ${f.ping?.error || '—'}`}
                    />
                    {f.rawProbe && (
                      <ForensicRow
                        label="Lock-free probe"
                        tone={rawProbeTone}
                        value={f.rawProbe.ok ? `${f.rawProbe.latencyMs}ms ✓ direct REST OK` : `failed: ${f.rawProbe.error || '—'}`}
                      />
                    )}
                    {f.verdict && (
                      <ForensicRow label="Verdict" tone={verdictTone} value={verdictLabel} />
                    )}
                    <ForensicRow
                      label="Realtime status"
                      tone={f.realtime.status === 'connected' ? 'good' : 'bad'}
                      value={`${f.realtime.status}${f.realtime.statusAgeMs != null ? ` · last set ${formatAge(f.realtime.statusAgeMs)}` : ''}`}
                    />
                    <ForensicRow
                      label="Channel states"
                      tone={channelsTone}
                      value={channelsLabel}
                    />
                    <ForensicRow label="Online" value={String(f.online)} tone={f.online ? 'good' : 'bad'} />
                    <ForensicRow label="Tab" value={f.visibility} tone="idle" />
                    <ForensicRow label="Queue size" value={String(f.pendingQueueCount)} tone={f.pendingQueueCount > 0 ? 'warn' : 'good'} />

                    {f.recentRestCalls.length > 0 && (
                      <details className="pt-1">
                        <summary className="text-[10.5px] text-text-muted cursor-pointer hover:text-text">
                          Last {f.recentRestCalls.length} REST call{f.recentRestCalls.length !== 1 ? 's' : ''}
                        </summary>
                        <ul className="mt-1 space-y-0.5 pl-2">
                          {f.recentRestCalls.map((c, i) => {
                            const slow = c.durationMs > 1500 || (c.ttfbMs != null && c.ttfbMs > 1500)
                            return (
                              <li key={i} className="text-[10px] font-mono text-text-faint leading-snug">
                                <span className={slow ? 'text-yellow-400' : ''}>
                                  {c.durationMs}ms
                                  {c.ttfbMs != null && <span> · TTFB {c.ttfbMs}ms</span>}
                                </span>
                                <span className="text-text-faint/70"> · {c.url.split('/rest/v1/')[1] || c.url}</span>
                              </li>
                            )
                          })}
                        </ul>
                      </details>
                    )}

                    {f.recentClientFetches?.length > 0 && (
                      <details className="pt-1">
                        <summary className="text-[10.5px] text-text-muted cursor-pointer hover:text-text">
                          Last {f.recentClientFetches.length} client fetch{f.recentClientFetches.length !== 1 ? 'es' : ''} (incl. aborts)
                        </summary>
                        <ul className="mt-1 space-y-0.5 pl-2">
                          {f.recentClientFetches.map((c, i) => (
                            <li key={i} className="text-[10px] font-mono text-text-faint leading-snug">
                              <span className={c.ok ? '' : 'text-red-400'}>
                                {c.ms}ms{c.ok ? ` · ${c.status}` : ` · ${c.aborted ? `aborted (${c.reason})` : 'failed'}`}
                              </span>
                              <span className="text-text-faint/70"> · {(c.url || '').split('/rest/v1/')[1] || (c.url || '').split('/').slice(-1)[0]}</span>
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}

                    <div className="pt-1.5">
                      <button
                        onClick={handleCopyForensics}
                        className="flex items-center gap-1.5 px-2 py-1 rounded-md border border-border text-text-muted text-[10.5px] font-medium hover:text-text hover:border-text-faint active:scale-[0.97] transition"
                      >
                        <Copy size={10} />
                        Copy diagnostics
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )
      })()}

      {/* ── Recent errors */}
      {errors.length > 0 && (
        <div className="border-b border-border">
          <div className="flex items-center justify-between px-3 py-2 border-b border-border/60">
            <div className="flex items-center gap-2">
              <AlertCircle size={12} className="text-red-400" />
              <span className="text-[11px] font-semibold text-red-400">Recent errors ({errors.length})</span>
            </div>
            <button
              onClick={onClearErrors}
              className="text-[10px] text-text-faint hover:text-text transition-colors flex items-center gap-0.5"
            >
              <Trash2 size={10} />
              Clear
            </button>
          </div>
          <ul className="max-h-40 overflow-y-auto divide-y divide-border/60">
            {errors.map((err) => (
              <li key={err.id} className="px-3 py-2 flex items-start gap-2">
                <span className="w-1 h-1 rounded-full bg-red-500 flex-shrink-0 mt-1.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] text-text leading-snug">{err.msg}</p>
                  <p className="text-[10px] text-text-faint mt-0.5">
                    {err.at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    {err.meta?.code && <span className="ml-1">· {err.meta.code}</span>}
                  </p>
                </div>
                <button
                  onClick={() => onDismissError(err.id)}
                  className="text-text-faint hover:text-text transition-colors flex-shrink-0 mt-0.5"
                  aria-label="Dismiss"
                >
                  <X size={11} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Retry action */}
      <div className="px-3 py-2.5 bg-surface-raised/50">
        <div className="flex items-center gap-2">
          <button
            onClick={handleRetry}
            disabled={retrying}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-accent text-bg text-[11px] font-medium hover:opacity-90 active:scale-[0.97] transition disabled:opacity-40 disabled:active:scale-100"
          >
            <RefreshCw size={11} className={retrying ? 'animate-spin' : ''} />
            {retrying ? 'Retrying…' : 'Retry now'}
          </button>
          {queue.length > 0 && (
            <button
              onClick={handleClearQueue}
              disabled={retrying}
              className="flex items-center gap-1.5 px-2 py-1 rounded-md border border-border text-text-muted text-[11px] font-medium hover:text-text hover:border-text-faint active:scale-[0.97] transition disabled:opacity-40 disabled:active:scale-100"
              title="Discard pending writes"
            >
              <Trash2 size={11} />
              Discard
            </button>
          )}
          {retryStatus && (
            <motion.span
              key={retryStatus.msg}
              initial={{ opacity: 0, x: -4 }}
              animate={{ opacity: 1, x: 0 }}
              className={`text-[10px] leading-snug flex-1 ${
                retryStatus.tone === 'good' ? 'text-green-400'
                : retryStatus.tone === 'warn' ? 'text-yellow-400'
                : retryStatus.tone === 'bad' ? 'text-red-400'
                : 'text-text-muted'
              }`}
            >
              {retryStatus.msg}
            </motion.span>
          )}
        </div>
        <p className="text-[10px] text-text-faint mt-1.5 leading-snug">
          Pending writes are saved to this device and sync automatically when the connection returns.
        </p>
      </div>
    </div>
  )
}
